/**
 * Session-based authentication utilities
 * Provides secure session management with automatic expiration
 */

const SESSION_TIMEOUT = 60 * 60 * 1000; // 1 hour in milliseconds
const TOKEN_KEY = 'adminToken';
const TIMESTAMP_KEY = 'adminLoginTime';
export const SESSION_ACTIVE_KEY = 'sessionActive'; // Flag to track active session

/**
 * Store authentication token with timestamp
 * @param {string} token - The authentication token
 */
export const setAuthToken = (token) => {
  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(TIMESTAMP_KEY, Date.now().toString());
  sessionStorage.setItem(SESSION_ACTIVE_KEY, 'true');
  // Clear any old localStorage tokens for security
  localStorage.removeItem(TOKEN_KEY);
};

/**
 * Get authentication token if session is still valid
 * @returns {string|null} - The token if valid, null if expired or not found
 */
export const getAuthToken = () => {
  const token = sessionStorage.getItem(TOKEN_KEY);
  const loginTimestamp = sessionStorage.getItem(TIMESTAMP_KEY);
  const sessionActive = sessionStorage.getItem(SESSION_ACTIVE_KEY);
  
  if (!token || !loginTimestamp || !sessionActive) {
    return null;
  }
  
  const currentTime = Date.now();
  const timeSinceLogin = currentTime - parseInt(loginTimestamp);
  
  if (timeSinceLogin >= SESSION_TIMEOUT) {
    // Session expired, clear tokens
    clearAuthTokens();
    return null;
  }
  
  return token;
};

/**
 * Clear all authentication tokens
 */
export const clearAuthTokens = () => {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TIMESTAMP_KEY);
  sessionStorage.removeItem(SESSION_ACTIVE_KEY);
  localStorage.removeItem(TOKEN_KEY); // Also clear any old localStorage tokens
};

/**
 * Check if user is authenticated with valid session
 * @returns {boolean} - True if authenticated and session is valid
 */
export const isAuthenticated = () => {
  return getAuthToken() !== null;
};

/**
 * Get remaining session time in milliseconds
 * @returns {number} - Remaining time in milliseconds, 0 if expired
 */
export const getRemainingSessionTime = () => {
  const loginTimestamp = sessionStorage.getItem(TIMESTAMP_KEY);
  
  if (!loginTimestamp) {
    return 0;
  }
  
  const currentTime = Date.now();
  const timeSinceLogin = currentTime - parseInt(loginTimestamp);
  const remainingTime = SESSION_TIMEOUT - timeSinceLogin;
  
  return Math.max(0, remainingTime);
};

/**
 * Extend session by updating the timestamp (call on user activity)
 */
export const extendSession = () => {
  const token = sessionStorage.getItem(TOKEN_KEY);
  if (token) {
    sessionStorage.setItem(TIMESTAMP_KEY, Date.now().toString());
  }
};

/**
 * Initialize session state on page load - clears session active flag
 * This ensures users must explicitly log in on each page refresh
 */
export const initializeSession = () => {
  sessionStorage.removeItem(SESSION_ACTIVE_KEY);
};

/**
 * Check if session was explicitly activated (user logged in)
 * @returns {boolean} - True if session is explicitly active
 */
export const isSessionExplicitlyActive = () => {
  return sessionStorage.getItem(SESSION_ACTIVE_KEY) === 'true';
};